import type { Cell } from "exceljs";
import { THIN_BORDER } from "../../dailyPrintout/render/cellStyle";
import { EMPTY_DAY_PLACEHOLDER, ENTRY_FONT_SIZE } from "../constants";
import type { DayEntry, WeekStaffScheduleExportColors } from "../types";

type WriteEntryCellArgs = {
	cell: Cell;
	entry: DayEntry | undefined;
	/** Only the first row of an empty day carries the placeholder; rows beneath it stay blank. */
	isFirstEntryRow: boolean;
	colors: WeekStaffScheduleExportColors;
};

const solidFill = (argb: string) => ({ type: "pattern" as const, pattern: "solid" as const, fgColor: { argb } });

/**
 * The lines shown inside one cell, stacked exactly like the on-screen card: the shift's time, then
 * its location, then its job. Exported so the row can be sized to the tallest cell it holds — a
 * narrow column clips anything the row height doesn't account for.
 */
export const entryLines = (entry: DayEntry): string[] => {
	switch (entry.kind) {
		case "pto":
			return [entry.timeOffLabel, entry.amountLabel];
		case "preference":
			return [entry.prefersLabel, entry.timeLabel];
		default:
			return [entry.timeLabel, entry.location, entry.jobName].filter((line): line is string => !!line);
	}
};

/** Font color and optional fill per entry kind — the whole reason each entry gets its own cell. */
const entryStyle = (entry: DayEntry, colors: WeekStaffScheduleExportColors): { fontArgb: string; fillArgb: string | undefined } => {
	switch (entry.kind) {
		case "scheduled":
			return { fontArgb: colors.scheduledFontArgb, fillArgb: colors.scheduledFillArgb };
		case "actual":
			return { fontArgb: colors.actualFontArgb, fillArgb: colors.actualFillArgb };
		default:
			// Time off and preferences read as muted context rather than a shift, so they carry no fill.
			return { fontArgb: colors.timeOffFontArgb, fillArgb: undefined };
	}
};

export const writeEntryCell = ({ cell, entry, isFirstEntryRow, colors }: WriteEntryCellArgs): void => {
	cell.border = THIN_BORDER;
	cell.alignment = { vertical: "top", horizontal: "left", wrapText: true };

	if (!entry) {
		cell.value = isFirstEntryRow ? EMPTY_DAY_PLACEHOLDER : "";
		cell.font = { size: ENTRY_FONT_SIZE, color: { argb: colors.dateRowFontArgb } };
		return;
	}

	const { fontArgb, fillArgb } = entryStyle(entry, colors);
	cell.value = entryLines(entry).join("\n");
	cell.font = { size: ENTRY_FONT_SIZE, bold: true, color: { argb: fontArgb } };
	if (fillArgb) cell.fill = solidFill(fillArgb);
};
